import React from 'react';
import { motion } from 'framer-motion';
import { Landmark, TrendingUp, ShieldAlert, ArrowRightLeft, Users, Clock, AlertCircle } from 'lucide-react';
import CustomSelect from '../../../components/ui/CustomSelect';
import Tooltip from '../../../components/ui/Tooltip';
import { RISK_PROFILES } from '../utils/allocationMath';

export default function AllocationHeader({
  clients = [],
  selectedClientId,
  onSelectClient,
  summary,
  targetProfile,
  onChangeProfile,
  horizonAnnees,
  onChangeHorizon
}) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const clientOptions = [
    { value: "all", label: "Modélisation libre (Profil type sans dossier)" },
    ...clients.map(c => {
      const typeLabel = c.isSociete ? "[Société]" : "[Foyer]";
      const name = c.denomination || c.raisonSociale || c.nom || c.nomFamille || "";
      const firstName = c.prenom || "";
      const ref = c.reference ? `(${c.reference})` : "";
      return {
        value: c.id || c._id,
        label: `${typeLabel} ${name} ${firstName} ${ref}`.trim()
      };
    })
  ];

  const profileOptions = Object.entries(RISK_PROFILES).map(([key, config]) => ({
    value: key,
    label: `${config.label} (${config.target.alternatif}% Alt / ${config.target.placements}% Marchés)`
  }));

  const currentProfileConfig = RISK_PROFILES[targetProfile] || RISK_PROFILES.Equilibre;

  return (
    <div className="space-y-6">
      {/* Barre de sélection Client & Profil de risque */}
      <div className="workspace-card p-6 bg-surface shadow-hp-sm border border-workspace rounded-2xl">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-center">
          {/* Sélection Client */}
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold uppercase tracking-wider text-subtle mb-1.5 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-secondary" />
              Dossier à Simuler
            </label>
            <CustomSelect
              value={selectedClientId || "all"}
              onChange={onSelectClient}
              options={clientOptions}
              placeholder="Sélectionner un dossier..."
              className="w-full"
            />
          </div>

          {/* Profil de Risque Cible */}
          <div className="md:col-span-4">
            <label className="block text-xs font-semibold uppercase tracking-wider text-subtle mb-1.5 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5 text-secondary" />
              Profil d'Investisseur Cible
              <Tooltip content="Définit la clé de répartition idéale entre actifs sécurisés, immobiliers, cotés et alternatifs.">
                <AlertCircle className="w-3.5 h-3.5 text-subtle cursor-pointer hover:text-primary" />
              </Tooltip>
            </label>
            <CustomSelect
              value={targetProfile}
              onChange={onChangeProfile}
              options={profileOptions}
              className="w-full"
            />
          </div>

          {/* Horizon d'investissement */}
          <div className="md:col-span-3">
            <label className="block text-xs font-semibold uppercase tracking-wider text-subtle mb-1.5 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-secondary" />
              Horizon Recommandé
            </label>
            <div className="h-11 px-3.5 py-2 rounded-[var(--hp-radius-md)] border border-workspace bg-surface-muted/30 flex items-center justify-between text-sm">
              <span className="font-semibold text-primary">{horizonAnnees || 8} ans</span>
              <span className="text-xs text-subtle">Moyen / Long terme</span>
            </div>
          </div>
        </div>

        {/* Description du profil sélectionné */}
        <div className="mt-4 pt-3.5 border-t border-workspace flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-subtle">
          <p>
            <strong className="text-primary">{currentProfileConfig.label} :</strong> {currentProfileConfig.description}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <span className="px-2 py-0.5 rounded-full bg-surface-muted text-primary font-medium">
              Cible Alt : {currentProfileConfig.target.alternatif}%
            </span>
            <span className="px-2 py-0.5 rounded-full bg-surface-muted text-primary font-medium">
              Cible Marchés : {currentProfileConfig.target.placements}%
            </span>
          </div>
        </div>
      </div>

      {/* Cartes KPI Synthétiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Patrimoine Brut */}
        <motion.div 
          whileHover={{ y: -2 }}
          className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-medium uppercase tracking-wider">Actifs Bruts Totaux</span>
            <Landmark className="w-4 h-4 text-info-600" />
          </div>
          <div>
            <p className="text-2xl font-black text-primary tracking-tight">{formatEuro(summary.totalActifsBruts)}</p>
            <p className="text-xs text-subtle mt-1">Actifs financiers, fonciers & pro</p>
          </div>
        </motion.div>

        {/* Endettement */}
        <motion.div 
          whileHover={{ y: -2 }}
          className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-medium uppercase tracking-wider">Passif & Emprunts</span>
            <ArrowRightLeft className="w-4 h-4 text-danger-500" />
          </div>
          <div>
            <p className="text-2xl font-black text-danger-600 tracking-tight">{formatEuro(summary.totalDettes)}</p>
            <p className="text-xs text-subtle mt-1">
              {summary.totalActifsBruts > 0 
                ? `Taux d'endettement : ${Math.round((summary.totalDettes / summary.totalActifsBruts) * 100)}%` 
                : "Aucune dette"}
            </p>
          </div>
        </motion.div>

        {/* Patrimoine Net */}
        <motion.div 
          whileHover={{ y: -2 }}
          className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col justify-between relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-2 h-full bg-secondary" />
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-medium uppercase tracking-wider">Patrimoine Net</span>
            <span className="w-2.5 h-2.5 rounded-full bg-secondary" />
          </div>
          <div>
            <p className="text-2xl font-black text-secondary tracking-tight">{formatEuro(summary.patrimoineNet)}</p>
            <p className="text-xs text-subtle mt-1">Valeur nette transmissible</p>
          </div>
        </motion.div>

        {/* Rendement Moyen Pondéré Estimé */}
        <motion.div 
          whileHover={{ y: -2 }}
          className="workspace-card p-5 bg-surface border border-workspace rounded-2xl shadow-hp-sm flex flex-col justify-between"
        >
          <div className="flex items-center justify-between text-subtle mb-2">
            <span className="text-xs font-medium uppercase tracking-wider">Rendement Est.</span>
            <TrendingUp className="w-4 h-4 text-success-600" />
          </div>
          <div>
            <p className="text-2xl font-black text-success-600 tracking-tight">+{summary.weightedYield}% <span className="text-xs text-subtle font-normal">/ an</span></p>
            <p className="text-xs text-subtle mt-1">Projection brute pondérée</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

